import React, { ComponentType, useMemo } from 'react';

import { BlockType, ElementType } from '../../types/api/ElementType';
import BlockFetcher from '../hocs/BlockFetcher/BlockFetcher';
import PasswordReset from './PasswordReset/PasswordReset';
import { ErrorBlock } from '../../_blocks/Error/ErrorBlock';
import { ChildPagesTreeBlock } from '../../_blocks/ChildPagesTree';
import { EventListDetailsBlock } from '../../_blocks/EventListDetails';
import EventDetails from './EventDetails/EventDetails';
import { EventIcalDownloadBlock } from '../../_blocks/EventIcalDownload/EventIcalDownloadBlock';
import { On2ContentBlock } from '../../_blocks/On2Content/On2ContentBlock';
import { PageLeadBlock } from '../../_blocks/PageLead/PageLeadBlock';
import { PageListDetailsBlock } from '../../_blocks/PageListDetails';
import { PageMetaInfoBlock } from '../../_blocks/PageMetaInfo';
import { PersonInteractionBlock } from '../../_blocks/PersonInteraction';
import { FaqListBlock } from '../../_blocks/FaqList';
import { ExternalLinkDetails } from './ExternalLinkDetails/ExternalLinkDetails';
import UserRegistration from './UserRegistration/UserRegistration';
import { UserRegistrationForm } from './UserRegistrationForm/UserRegistrationForm';
import SingleInputBlock from './SingleInputBlock/SingleInputBlock';
import { SearchResults } from './SearchResults';
import BlockContext from './BlockContext';
import { PortalThemeHighlightColorBlock } from '../../_blocks/PortalThemeHighlightColor/PortalThemeHighlightColorBlock';
import { ContentWrapper } from './ContentWrapper/ContentWrapper';
import { VideoBlock } from '../../_blocks/VideoBlock';
import { AccessKeysBlock } from '../../_blocks/AccessKeys/AccessKeysBlock';
import { TopHeadlinesArchiveBlock, TopHeadlinesBlock } from '../../_blocks/TopHeadlines';
import { PageTypesListBlock } from '../../_blocks/PageTypesList/PageTypesListBlock';
import { UserToPageRolesListBlock } from '../../_blocks/UserToPageRolesList';
import { UserToPageRolePageLeadBlock } from '../../_blocks/UserToPageRolePageLead';
import { PageMembershipMatchingRuleListBlock } from '../../_blocks/PageMembershipMatchingRuleList';
import { PageMembershipMatchingRulePageLeadBlock } from '../../_blocks/PageMembershipMatchingRulePageLead';
import { LoginDescriptionBlock } from '../../_blocks/LoginDescription';
import { RichTextBlock } from '../../_blocks/RichText';
import { ProfileImageBlock } from '../../_blocks/ProfileImage/ProfileImageBlock';
import { PortalLogoBlock } from '../../_blocks/PortalLogo/PortalLogoBlock';
import { PortalAvatarBlock } from '../../_blocks/PortalAvatar/PortalAvatarBlock';
import { TitleImageBlock } from '../../_blocks/TitleImage/TitleImageBlock';
import { ContentImageBlock } from '../../_blocks/ContentImage/ContentImageBlock';
import { ThreadBlock } from '../../_blocks/Thread';
import { BackToParentBlock } from '../../_blocks/BackToParent/BackToParent';
import { PageToPageBlock } from '../../_blocks/PageToPage';
import { ThreadListBlock } from '../../_blocks/ThreadList';
import { ExternalUserRegistrationBlock } from '../../_blocks/ExternalUserRegistration';
import { KeywordIndexBlock } from '../../_blocks/KeywordIndex';
import {
  AnnouncementsContextBlock,
  AnnouncementsInboxBlock,
  AnnouncementsOutboxBlock,
} from '../../_blocks/Announcements';
import { UserRegistrationPresetsBlock } from '../../_blocks/UserRegistrationPresets/UserRegistrationPresetsBlock';
import { SystemPageStatisticsBlock } from '../../_blocks/SystemPageStatistics/SystemPageStatisticsBlock';
import { AttributeGroupsListBlock } from '../../_blocks/AttributeGroupsList/AttributeGroupsListBlock';
import { PageTypePageLeadBlock } from '../../_blocks/PageTypePageLead';
import { AttributeGroupPageLeadBlock } from '../../_blocks/AttributeGroupPageLead';
import { TicketBookingBlock } from '../../_blocks/TicketBooking/TicketBookingBlock';
import { SharedFilesBlock } from '../../_blocks/SharedFiles';
import { ExternalContentBlock } from '../../_lib/components/ExternalContent';
import { LazyLoadSurveyBlock } from '../../_blocks/Survey';
import { LazyLoadFormApplicationBlock } from '../../_blocks/FormApplication';
import { MaxFileSizeConfigBlock } from '../../_blocks/MaxFileSizeConfig';
import { NewsletterfooterBlock } from '../../_blocks/Newsletterfooter';
import { PersonProfileEditHintConfigBlock } from '../../_blocks/PersonProfileEditHintConfig';
import { PersonalDataProcessingTextBlock } from '../../_blocks/PersonalDataProcessingText';
import { RegistrationEmailBlock } from '../../_blocks/RegistrationEmail';
import { RegistrationValidityTextBlock } from '../../_blocks/RegistrationInvitationEmail';
import { MembersBlock } from '../../_blocks/Members/MembersBlock';
import { TimeControlledPublicationInfoBlock } from '../../_blocks/TimeControlledPublicationInfo';
import { PageArchivedInfoBlock } from '../../_blocks/PageArchivedInfo';
import { UserSearchBlock } from '../../_blocks/Search/UserSearchBlock';
import { ContentSearchBlock } from '../../_blocks/Search/ContentSearchBlock';
import { EditorialDashboardBlock } from '../../_blocks/Search/EditorialDashboardBlock';
import { AdditionalPrivilegeConfigBlock } from '../../_blocks/PrivilegeConfig/AdditionalPrivilegeConfigBlock';
import { BasicPrivilegeConfigBlock } from '../../_blocks/PrivilegeConfig/BasicPrivilegeConfigBlock';
import { PageValidityExpiredInfoBlock } from '../../_blocks/PageValidityExpiredInfo';
import { PageTrashBlock } from '../../_blocks/PageTrash';
import { CollapsibleContainerBlock } from '../../_blocks/CollapsibleContainerBlock/CollapsibleContainerBlock';

import {
  ProposedPagePublicationInfoBlock,
} from '../../_blocks/ProposedPagePublicationInfo/ProposedPagePublicationInfo';
import { LatestRegistrationsBlock } from '../../_blocks/LatestRegistrations/LatestRegistrationsBlock';
import { ChangeEmailAddressBlock } from '../../_blocks/ChangeEmailAddressBlock';
import { TeamRoomMemberRequestsBlock } from '../../_blocks/TeamRoomMemberRequests/TeamRoomMemberRequestsBlock';
import { UserToUserRolesListBlock } from '../../_blocks/UserToUserRolesList';
import { PageMembershipMatchingRuleReportsBlock } from '../../_blocks/PageMembershipMatchingRuleReports';
import { FormApplicationNotificationsBlock } from '../../_blocks/FormApplicationNotifications';
import { TeamroomManagementBlock } from './TeamroomManagement';
import { UserImportReportsBlock } from '../../_blocks/UserImportReports/UserImportReportsBlock';

/**
 * @Deperecated
 * use getPureWidget in widgetsToComponents for now.
 */
const Blocks = {
  [BlockType.areamembers]: MembersBlock,
  [ElementType.contentWrapper]: BlockFetcher()(ContentWrapper),

  [ElementType.searchlist]: BlockFetcher()(SearchResults),
  [ElementType.passwordforgotform]: BlockFetcher()(PasswordReset),
  [ElementType.passwordresetform]: BlockFetcher()(PasswordReset),

  [BlockType.eventlistdetails]: EventListDetailsBlock,
  [ElementType.eventdetails]: BlockFetcher()(EventDetails),
  [ElementType.externalcontent]: ExternalContentBlock,

  [BlockType.faqlist]: FaqListBlock,
  [BlockType.teamroommanagement]: TeamroomManagementBlock,
  [BlockType.teamroommemberrequests]: TeamRoomMemberRequestsBlock,
  [ElementType.externallinkdetails]: BlockFetcher()(ExternalLinkDetails),

  [BlockType.usertopageroleslist]: UserToPageRolesListBlock,
  [BlockType.usertouserroleslist]: UserToUserRolesListBlock,
  [BlockType.pagemembershipmatchingrulelist]: PageMembershipMatchingRuleListBlock,
  [BlockType.pagemembershipmatchingrulereports]: PageMembershipMatchingRuleReportsBlock,
  [BlockType.pagemembershipmatchingrulepagelead]: PageMembershipMatchingRulePageLeadBlock,
  [BlockType.systempagestatistics]: SystemPageStatisticsBlock,

  [ElementType.userregistration]: BlockFetcher()(UserRegistration),
  [ElementType.userregistrationform]: BlockFetcher()(UserRegistrationForm),

  // Single Inputs Forms
  [BlockType.allowedmimetypes]: SingleInputBlock,
  [BlockType.portaltitle]: SingleInputBlock,
  [BlockType.changepasswordurl]: SingleInputBlock,
  [BlockType.supportEmail]: SingleInputBlock,
  [BlockType.portalInformationLink]: SingleInputBlock,
  [BlockType.goliveMode_enabled]: SingleInputBlock,
  [BlockType.goliveMode_userGroupId]: SingleInputBlock,
  [BlockType.anonymousaccess_enabled]: SingleInputBlock,
  [BlockType.userregistration_enabled]: SingleInputBlock,
  [BlockType.inviteunregistered_enabled]: SingleInputBlock,
  [BlockType.portaluserregistration_enabled]: SingleInputBlock,
  [BlockType.sessionrestrictor_enabled]: SingleInputBlock,
  [BlockType.highestConfidentialLevel]: SingleInputBlock,
  [BlockType.defaultConfidentialLevel]: SingleInputBlock,
  [BlockType.printingDependsOnConfidentialLevels]: SingleInputBlock,
  [BlockType.extendedProtocol_readAccess_enabled]: SingleInputBlock,
  [BlockType.extendedProtocol_writeAccess_adminContent_enabled]: SingleInputBlock,
  [BlockType.extendedProtocol_writeAccess_userContent_enabled]: SingleInputBlock,
  [BlockType.defaultForMembersWithParentTask]: SingleInputBlock,
  [BlockType.defaultForMembersWithParentTeamroom]: SingleInputBlock,
  [BlockType.defaultForMembersWithParentTopic]: SingleInputBlock,
  [BlockType.defaultForMembersWithParentOrgunit]: SingleInputBlock,
  [BlockType.defaultForMembersWithParentLocation]: SingleInputBlock,
  [BlockType.defaultForMembersWithParentProcess]: SingleInputBlock,
  [BlockType.defaultForNewsletterFrequency]: SingleInputBlock,
  [BlockType.depublishPagesWithExceededValidityPeriod_enabled]: SingleInputBlock,
  [BlockType.pageLockValidityPeriod]: SingleInputBlock,
  [BlockType.userImportSystemService_enabled]: SingleInputBlock,
  [BlockType.pageMembershipMatcherSystemService_enabled]: SingleInputBlock,

  [BlockType.portalThemeHighlightColor]: PortalThemeHighlightColorBlock,
  [BlockType.cspFrameSourceConfig]: SingleInputBlock,

  [BlockType.video]: VideoBlock,
  [BlockType.usersearch]: UserSearchBlock,
  [BlockType.contentsearch]: ContentSearchBlock,
  [BlockType.editorialdashboard]: EditorialDashboardBlock,

  // Image uploads
  [BlockType.profileimage]: ProfileImageBlock,
  [BlockType.portalavatar]: PortalAvatarBlock,
  [BlockType.portallogo]: PortalLogoBlock,
  [BlockType.titleimage]: TitleImageBlock,
  [BlockType.contentimage]: ContentImageBlock,
  // RichText Blocks to Edit and Show HTML
  [BlockType.richtext]: RichTextBlock,
  [BlockType.logindescription]: LoginDescriptionBlock,
  [BlockType.userregistrationpresets]: UserRegistrationPresetsBlock,

  [BlockType.pagelistdetails]: PageListDetailsBlock,
  [BlockType.personinteraction]: PersonInteractionBlock,
  [BlockType.topheadlines]: TopHeadlinesBlock,
  [BlockType.topheadlinesarchive]: TopHeadlinesArchiveBlock,
  [BlockType.thread]: ThreadBlock,
  [BlockType.threadlist]: ThreadListBlock,
  [BlockType.backtoparent]: BackToParentBlock,
  [BlockType.pageToPage]: PageToPageBlock,
  [BlockType.externalUserRegistration]: ExternalUserRegistrationBlock,
  [BlockType.childpagestree]: ChildPagesTreeBlock,
  [BlockType.keywordindex]: KeywordIndexBlock,
  [BlockType.on2content]: On2ContentBlock,
  [BlockType.allannouncementsinbox]: AnnouncementsInboxBlock,
  [BlockType.allannouncementsoutbox]: AnnouncementsOutboxBlock,
  [BlockType.currentannouncementsinbox]: AnnouncementsContextBlock,
  [BlockType.pagemetainfo]: PageMetaInfoBlock,
  [BlockType.eventicaldownload]: EventIcalDownloadBlock,
  [BlockType.attributegroupslist]: AttributeGroupsListBlock,
  [BlockType.pagelead]: PageLeadBlock,
  [BlockType.usertopagerolepagelead]: UserToPageRolePageLeadBlock,
  [BlockType.usertouserrolepagelead]: UserToPageRolePageLeadBlock,
  [BlockType.attributegrouppagelead]: AttributeGroupPageLeadBlock,
  [BlockType.pagetypeslist]: PageTypesListBlock,
  [BlockType.pagetypepagelead]: PageTypePageLeadBlock,
  [BlockType.accesskeys]: AccessKeysBlock,
  [BlockType.error]: ErrorBlock,
  [BlockType.survey]: LazyLoadSurveyBlock,
  [BlockType.formapplication]: LazyLoadFormApplicationBlock,
  [BlockType.formapplicationNotifications]: FormApplicationNotificationsBlock,
  [BlockType.ticketbooking]: TicketBookingBlock,
  [BlockType.sharedfiles]: SharedFilesBlock,
  [BlockType.maxFileSizeConfig]: MaxFileSizeConfigBlock,
  [BlockType.newsletterfooter]: NewsletterfooterBlock,
  [BlockType.personProfileEditHintConfig]: PersonProfileEditHintConfigBlock,
  [BlockType.personalDataProcessingText]: PersonalDataProcessingTextBlock,
  [BlockType.registrationEmail]: RegistrationEmailBlock,
  [BlockType.registrationvalidity]: RegistrationValidityTextBlock,
  [BlockType.additionalprivilegeconfig]: AdditionalPrivilegeConfigBlock,
  [BlockType.basicprivilegeconfig]: BasicPrivilegeConfigBlock,
  [BlockType.timecontrolledpublicationinfo]: TimeControlledPublicationInfoBlock,
  [BlockType.proposedpagepublicationinfo]: ProposedPagePublicationInfoBlock,
  [BlockType.pageArchivedInfo]: PageArchivedInfoBlock,
  [BlockType.pagevalidityexpiredinfo]: PageValidityExpiredInfoBlock,
  [BlockType.pagetrash]: PageTrashBlock,
  [BlockType.latestRegistrations]: LatestRegistrationsBlock,
  [BlockType.changeEmailAddress]: ChangeEmailAddressBlock,
  [BlockType.collapsiblecontainer]: CollapsibleContainerBlock,
  [BlockType.userimportreports]: UserImportReportsBlock,
};

/**
 * @Deprecated
 */
const BlocksWithContext = Object.entries(Blocks).reduce<Record<string, ComponentType<{ uuid: string }>>>(
  (accumulator, [elementType, Block]) => {
    function BlockContextWrapper(props: { uuid: string }) {
      const value = useMemo(
        () => ({
          elementType: elementType as ElementType,
          uuid: props.uuid,
        }),
        [props.uuid],
      );
      return (
        <BlockContext.Provider value={value}>
          {/* @ts-expect-error complexity is generally very high */}
          <Block {...props} />
        </BlockContext.Provider>
      );
    }

    return { ...accumulator, [elementType]: BlockContextWrapper };
  },
  {},
);

export default BlocksWithContext;
